import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DownloadButton } from "@/components/tracks/DownloadButton";
import { useNavigate, useParams } from "react-router-dom";
import { CoverImg } from "@/components/ui/CoverImg";
import { TrackList } from "@/components/tracks/TrackList";
import { musicService } from "@/services/musicService";
import { usePlayer } from "@/hooks/usePlayer";
import { useCoverColor } from "@/hooks/useCoverColor";
import { useStickyHeaderTrigger } from "@/hooks/useStickyHeaderTrigger";
import { usePreloadImage } from "@/hooks/usePreloadImage";
import { coverUrl } from "@/lib/cover";
import { fmtDuration } from "@/utils/format";
import { addNotInterested, getNotInterested } from "@/lib/notInterested";
import { sfx } from "@/lib/sfx";
import type { Playlist, Track } from "@/types/types";

export function PlaylistPage() {
  const { id = "" } = useParams();
  const nav = useNavigate();
  const [p, setP] = useState<Playlist | null>(null);
  const [tracks, setTracks] = useState<Track[]>([]);
  const [suggestions, setSuggestions] = useState<Track[] | null>(null);
  const [enhancing, setEnhancing] = useState(false);
  const shownRef = useRef<Set<string>>(new Set());
  const [addingId, setAddingId] = useState<string | null>(null);
  const [justAdded, setJustAdded] = useState<Set<string>>(new Set());
  const player = usePlayer();
  const wash = useCoverColor(p?.cover_id);
  const { sentinelRef, visible: stickyVisible } = useStickyHeaderTrigger();
  usePreloadImage(p?.cover_id ? coverUrl(p.cover_id, 232) : null);

  const loadTracks = useCallback(() => {
    musicService.playlistTracks(id).then(setTracks).catch(console.error);
  }, [id]);

  useEffect(() => {
    musicService.playlist(id).then(setP).catch(console.error);
    loadTracks();
    setSuggestions(null);
    shownRef.current = new Set();
    setJustAdded(new Set());
  }, [id, loadTracks]);

  const existing = useMemo(() => new Set(tracks.map((t) => t.id)), [tracks]);
  const visibleSuggestions = useMemo(
    () => (suggestions ?? []).filter((t) => !existing.has(t.id) || justAdded.has(t.id)),
    [suggestions, existing, justAdded],
  );

  /** Spotify "Enrichir": songs that fit this playlist, to keep or dismiss. */
  const enhance = useCallback(async () => {
    if (!tracks.length || enhancing) return;
    setEnhancing(true);
    sfx.pop();
    try {
      const exclude = [...tracks.map((t) => t.id), ...getNotInterested(), ...shownRef.current];
      const recs = await musicService.forTracks(tracks.map((t) => t.id), exclude, 12);
      recs.forEach((t) => shownRef.current.add(t.id));
      setSuggestions(recs);
    } catch (e) {
      console.error(e);
      setSuggestions([]);
    } finally { setEnhancing(false); }
  }, [tracks, enhancing]);

  const dismiss = useCallback((t: Track) => {
    sfx.tap();
    addNotInterested(t.id);
    setSuggestions((s) => (s ?? []).filter((x) => x.id !== t.id));
  }, []);

  const addAll = useCallback(async () => {
    const todo = visibleSuggestions.filter((t) => !justAdded.has(t.id));
    if (!todo.length) return;
    try {
      await musicService.addTracksToPlaylist(id, todo.map((t) => t.id));
      sfx.chirp();
      setJustAdded((s) => new Set([...s, ...todo.map((t) => t.id)]));
      loadTracks();
    } catch (e) { console.error(e); }
  }, [visibleSuggestions, justAdded, id, loadTracks]);

  const quickAdd = useCallback(async (t: Track) => {
    setAddingId(t.id);
    try {
      await musicService.addTracksToPlaylist(id, [t.id]);
      setJustAdded((s) => { const n = new Set(s); n.add(t.id); return n; });
      loadTracks();
    } catch (e) { console.error(e); }
    finally { setAddingId(null); }
  }, [id, loadTracks]);

  const deletePlaylist = useCallback(async () => {
    if (!p) return;
    if (!window.confirm(`Supprimer "${p.name}" ? C'est définitif.`)) return;
    try {
      await musicService.deletePlaylist(id);
      nav("/library");
    } catch {
      window.alert("Suppression impossible.");
    }
  }, [id, p, nav]);

  if (!p) return null;
  return (
    <div className="page-hero-wrap">
      <div className="hero-wash" style={{
        background: wash
          ? `linear-gradient(180deg, ${wash} 0%, transparent 65%)`
          : "transparent",
      }} />

      <div className={"page-sticky-header" + (stickyVisible ? " visible" : "")}>
        <CoverImg coverId={p.cover_id} alt={p.name} size={36} />
        <strong className="trunc">{p.name}</strong>
        <div className="grow" />
        <button className="btn primary" style={{ padding: "8px 14px" }}
                onClick={() => void player.play(tracks, 0)}>▶</button>
      </div>

      <header className="hero" style={{
        display: "flex", flexDirection: "row", alignItems: "flex-end",
        gap: 28, marginBottom: 28,
      }}>
        <CoverImg coverId={p.cover_id} alt={p.name} size={232} priority />
        <div style={{ display: "flex", flexDirection: "column", gap: 10, paddingBottom: 6, minWidth: 0 }}>
          <div className="muted" style={{ fontSize: 13 }}>Playlist</div>
          <h1 style={{ margin: 0, fontSize: 52, lineHeight: 1.05, fontWeight: 800 }}>{p.name}</h1>
          {p.description && <div className="muted" style={{ fontSize: 14 }}>{p.description}</div>}
          <div className="row" style={{ marginTop: 10, gap: 8 }}>
            <button className="btn primary"
                    onClick={() => void player.play(tracks, 0)}>▶ Lire</button>
            <button className="btn" title="Lecture aléatoire avec des recommandations ✦ glissées entre tes titres"
                    onClick={() => { sfx.pop(); void player.playSmartShuffle(tracks); }}
                    disabled={!tracks.length}>✦ Aléatoire intelligent</button>
            <button className="btn" onClick={() => void enhance()} disabled={!tracks.length || enhancing}
                    title="Des titres qui collent à l'ambiance de cette playlist">
              {enhancing ? "…" : "✨ Enrichir"}
            </button>
            <DownloadButton id={`playlist:${p.id}`} label={p.name} tracks={tracks} />
            <button className="btn" onClick={() => void deletePlaylist()}>Supprimer</button>
          </div>
        </div>
      </header>
      <div ref={sentinelRef} style={{ height: 1 }} aria-hidden="true" />

      <TrackList tracks={tracks} />

      {suggestions !== null && (
        <section className="enhance-panel">
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
            <div>
              <h2 style={{ margin: 0 }}>✨ Pour enrichir cette playlist</h2>
              <span className="muted" style={{ fontSize: 13 }}>
                Des titres qui collent à l'ambiance. Ajoute ceux qui te plaisent, ✕ pour ne plus les revoir.
              </span>
            </div>
            <div className="row" style={{ gap: 8 }}>
              {visibleSuggestions.some((t) => !justAdded.has(t.id)) && (
                <button className="btn" onClick={() => void addAll()}>Tout ajouter</button>
              )}
              <button className="btn" onClick={() => void enhance()} disabled={enhancing}>
                {enhancing ? "…" : "↻ Autres"}
              </button>
              <button className="btn ghost" onClick={() => setSuggestions(null)} aria-label="Fermer">✕</button>
            </div>
          </div>
          {visibleSuggestions.length === 0 && !enhancing && (
            <p className="muted" style={{ marginTop: 14 }}>Plus de suggestions pour l'instant — clique sur « Autres ».</p>
          )}
          <div style={{ display: "flex", flexDirection: "column", marginTop: 12 }}>
            {visibleSuggestions.map((t) => {
              const added = justAdded.has(t.id);
              const busy = addingId === t.id;
              return (
                <div key={t.id} className="row" style={{ padding: "8px 10px", borderRadius: 8, gap: 12,
                                                          alignItems: "center" }}>
                  <div style={{ width: 44, height: 44 }}>
                    <CoverImg coverId={t.album?.cover_id} alt={t.title} size={44} />
                  </div>
                  <button className="grow" style={{ textAlign: "left" }}
                          onClick={() => void player.playOne(t)}>
                    <div className="trunc">{t.title}</div>
                    <div className="muted trunc">{t.artist.name}</div>
                  </button>
                  <span className="muted" style={{ minWidth: 46, textAlign: "right" }}>
                    {fmtDuration(t.duration_ms)}
                  </span>
                  {!added && (
                    <button title="Pas intéressé" onClick={() => dismiss(t)}
                            className="enhance-dismiss">✕</button>
                  )}
                  <button
                    title={added ? "Ajouté à la playlist" : "Ajouter à cette playlist"}
                    onClick={() => void quickAdd(t)}
                    disabled={added || busy}
                    style={{
                      fontSize: 18, lineHeight: 1, padding: "4px 12px", borderRadius: 999,
                      border: "1px solid var(--line)",
                      background: added ? "var(--accent)" : "transparent",
                      color: added ? "#000" : "var(--fg)",
                      cursor: added ? "default" : "pointer",
                      minWidth: 40,
                    }}
                  >
                    {busy ? "…" : added ? "✓" : "+"}
                  </button>
                </div>
              );
            })}
          </div>
        </section>
      )}
    </div>
  );
}
